import { action, makeObservable, observable, runInAction, toJS } from "mobx";
import menu, { MenuData, MenuOption } from "../api/menu";
import { ExpandableKeys, WithBooleanFlag } from "./core";
import { TreeNode } from "../ui-kit/ITreeSelect/ITreeSelect";
import menuApi, { MenuItem } from "../api/menu";
import { StatusInfo } from "./core/StatusInfo";
import Constants from "../constants/Common";
import FlatToNested from "flat-to-nested";
import { pick } from "lodash";
import { INavigationItem } from "../interface/menu";

class Menu {
  flatMenu: INavigationItem[] = [];
  flatMenuWithDisabled: INavigationItem[] = [];
  menu: MenuData = [];
  menuOptions: Array<MenuOption> = [];
  menuWithDisabledLoading = new WithBooleanFlag(false);
  menuIsLoading = new WithBooleanFlag();
  menuIsCollapsed = new WithBooleanFlag(true);
  menuTree: TreeNode[] = [];
  menuTreeWithDisabled: TreeNode[] = [];
  menuStatus = new StatusInfo();
  paramRowKeys: ExpandableKeys = new ExpandableKeys();
  isResetMenu = new WithBooleanFlag(false);

  elementMouseOn: HTMLElement = null;

  constructor() {
    makeObservable(
      this,
      {
        clearMenu: action,
        flatMenu: observable,
        flatMenuWithDisabled: observable,
        menuWithDisabledLoading: observable,
        getMenu: action,
        getMenuTree: action,
        menu: observable,
        menuOptions: observable,
        menuTree: observable,
        menuTreeWithDisabled: observable,
        saveNavigationItem: action,
        deleteMenuItem: action,
        updateNavigationItem: action,
        isResetMenu: observable,
      },
      {
        deep: true,
      },
    );
  }

  getMenu = async () => {
    this.menuIsLoading.setTrue();

    try {
      const res = await menu.getMenu();

      const cb = (item) => {
        if (!!item.items.length) {
          item.items = item.items.map(cb);
        }
        return { ...item, keys: new ExpandableKeys() };
      };

      runInAction(() => {
        this.menu = res.data.map(cb);
        this.menuOptions = this.getFlatMenu(res.data);
      });
    } catch (e) {
      console.log(e);
    } finally {
      this.menuIsLoading.setFalse();
    }
  };

  getMenuWithDisabled = async () => {
    try {
      runInAction(() => {
        this.menuWithDisabledLoading.setTrue();
      });

      const { data } = await menuApi.getRemovableMenu(); // getFlatMenu

      // const flatToNested = new FlatToNested({
      //   id: "id",
      //   parent: "parentId",
      //   children: "children",
      // });
      // console.log("flatToNested", flatToNested);

      // const menuTreeWithDisabled = flatToNested.convert(
      //   data.map((i) => {
      //     i.value = i.id;
      //     i.title = i.name;
      //     return i;
      //   }),
      // );

      runInAction(() => {
        this.menuTreeWithDisabled = data.map((i) => ({
          id: i.id,
          title: i.name,
          url: i.url,
          name: i.name,
        }));
        this.flatMenuWithDisabled = data;
      });
    } catch (err) {
      runInAction(() => {
        this.menuStatus.setDetails(err);
        this.menuStatus.setStatus(Constants.requestStatuses.error);
      });
    } finally {
      runInAction(() => {
        this.menuWithDisabledLoading.setFalse();
      });
    }
  };

  deleteMenuItem = async (id) => {
    this.menuStatus.resetStatus();
    try {
      await menuApi.deleteMenuItem(id);
      await this.getMenu();
      await this.getMenuWithDisabled();
    } catch (e) {
      this.menuStatus.setStatus(Constants.requestStatuses.error);
    }
  };

  getFlatMenu(data, level = 0) {
    const menu = data.reduce((prev, curr) => {
      const item = {
        id: curr.id,
        name: `${"-".repeat(level)} ${curr.name}`,
        level: level,
      };
      if (curr.items.length) {
        const menu = this.getFlatMenu(curr.items, level + 1);
        prev = [...prev, item, ...menu];
      } else {
        prev.push(item);
      }

      return prev;
    }, []);

    return menu;
  }

  // Формируется дерево для ITreeSelect. Запрос на сервер не делается, берется меню из стора, поэтому
  // этот метод можно вызвать в компоненте, когда мы уже подтянули в стор меню через getMenu
  getMenuTree = async (
    onlyLeafsCanBeSelected = true,
    idsToExclude: number[] = [],
  ) => {
    const flat = [];

    function recursive(menu): (TreeNode & MenuItem)[] {
      return menu
        .filter((i) => {
          return !idsToExclude.includes(i.id);
        })
        .map((item) => {
          if (item.items?.length) {
            item.selectable = !onlyLeafsCanBeSelected;
            item.children = recursive(item.items);
          }
          item.title = item.name;
          item.value = item.id;
          flat.push(toJS(item));
          return item;
        });
    }

    const tree = recursive(this.menu);

    runInAction(() => {
      this.menuTree = tree;
      this.flatMenu = flat;
    });
  };

  async saveNavigationItem(values) {
    try {
      const res = await menu.addMenuItem({ ...values, enabled: true });

      return { ...res, success: true };
    } catch (e) {
      return { ...e.response, success: false };
    }
  }

  updateNavigationItem = async (navigationItem: INavigationItem) => {
    this.menuStatus.resetStatus();
    const values = pick(navigationItem, [
      "parentId",
      "name",
      "url",
      "icon",
      "orderNum",
      "enabled",
    ]);

    try {
      await menu.updateMenuItem(navigationItem.id, values);
      this.menuStatus.setStatus(Constants.statusMessage.updated);
      await this.getMenu();
      await this.getMenuWithDisabled();
    } catch (e) {
      runInAction(() => {
        this.menuStatus.setDetails(e);
        this.menuStatus.setStatus(Constants.requestStatuses.error);
      });
    }
  };

  clearMenu = () => {
    runInAction(() => {
      this.menu = [];
    });
  };

  setElementMouseOn = (el: HTMLElement) => {
    this.elementMouseOn = el;
  }
}

const menuStore = new Menu();
export default menuStore;
